import { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import CabeceraCliente from '../CabeceraCliente.js';
import TableDetalleCotizacion from '../../../cotizacion/components/TableDetalleCotizacion.js';
import ObservacionesEntregaResumen from '../../../cotizacion/components/ObservacionesEntregaResumen.js';
import BotoneraCotizacion from '../../../cotizacion/components/BotoneraCotizacion.js';
import { mapDetalleResponseToLines, applyExistenciaStocks } from '../../../../utilities/cotizacionUtils.js';
import { getdetalle, getResumen, eliminarProducto, actualizarCantidad, actualizarDescuento, actualizarPrecio, getAlmacen, existenciaCotizacion, actualizarEntrega, actualizarDespacho } from '../../../../service/AffariService.js';
import LoaderTransparent from '../../../../components/ui/LoaderTransparent.js';
import TableProductos from '../../../cotizacion/components/TableProductos.js';

export const EditarCotizacionDialog = ({ visible, onCancel, cotizacion, toast }) => {
    const idCotizacion = cotizacion?.id;
    const tipo = cotizacion?.estado?.tipo;
    const [detallesState, setDetallesState] = useState([]);
    const [almacenes, setAlmacenes] = useState([]);
    const [confirming, setConfirming] = useState(true);
    const [edit] = useState(true);
    const [data, setData] = useState(cotizacion || {});
    const [resumen, setResumen] = useState({});
    const [loading, setLoading] = useState(false);
    const [visibleProductosTable, setVisibleProductosTable] = useState(false);

    const notify = useCallback((severity, summary, detail) => {
        if (toast && toast.current) {
            toast.current.show({ severity, summary, detail });
        }
    }, [toast]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        if (!idCotizacion) return;
        try {
            const detalleResponse = await getdetalle(false, false, idCotizacion, tipo);
            let lines = mapDetalleResponseToLines(detalleResponse);

            try {
                const existencia = await existenciaCotizacion(idCotizacion);
                if (existencia) {
                    const { updatedLines, almacenesList } = applyExistenciaStocks(existencia, lines);
                    lines = updatedLines;
                    setAlmacenes(almacenesList);
                }
            } catch (error) {
                console.error('Error cargando almacenes:', error);
            }

            console.log(lines);
            setDetallesState(lines);

            try {
                const resumenResponse = await getResumen(idCotizacion);
                setResumen(resumenResponse || {});
            } catch (error) {
                console.error('Error cargando resumen:', error);
                notify('error', 'Error cargando resumen', error.message || '');
            }
        } catch (error) {
            console.error('Error cargando datos de cotización:', error);
            //notify('error', 'Error cargando datos', error.message || '');
        } finally {
            setLoading(false);
        }
    }, [idCotizacion, tipo, notify]);

    useEffect(() => {
        if (!visible || !idCotizacion) return;
        setData(cotizacion || {});
        fetchData();
    }, [visible, idCotizacion, cotizacion, fetchData]);

    const handleRefresh = async () => {
        setLoading(true);
        if (!idCotizacion) return;
        try {
            const detalleResponse = await getdetalle(false, false, idCotizacion, tipo);
            let lines = mapDetalleResponseToLines(detalleResponse);

            try {
                const existencia = await existenciaCotizacion(idCotizacion);
                if (existencia) {
                    const { updatedLines, almacenesList } = applyExistenciaStocks(existencia, lines);
                    lines = updatedLines;
                    setAlmacenes(almacenesList);
                }
            } catch (error) {
                console.error('Error recargando existencia:', error);
            }

            const resumenResponse = await getResumen(idCotizacion);
            setResumen(resumenResponse || {});

            setDetallesState(lines);
        } catch (error) {
            console.error('Error recargando datos:', error);
            notify('error', 'Error recargando datos', error.message || '');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveLine = async (rowIndex) => {
        setLoading(true);
        const line = detallesState[rowIndex];
        if (!line || !line.id) return;
        try {
            await eliminarProducto(line.id);
            await handleRefresh();
        } catch (error) {
            console.error('Error eliminando producto:', error);
            notify('error', 'Error eliminando producto', 'No se pudo eliminar el producto');
        } finally {
            setLoading(false);
        }
    };

    const handleChangeCantidad = async (cantidad, idDetalle) => {
        setLoading(true);
        if (!idDetalle) return;
        try {
            await actualizarCantidad(cantidad ?? 0, idDetalle);
            await handleRefresh();
        } catch (error) {
            console.error('Error actualizando cantidad:', error);
            notify('error', 'Error actualizando cantidad', error.message || '');
        } finally {
            setLoading(false);
        }
    };

    const onChangeCondicionEntrega = async (value) => {
        setLoading(true);
        setData((prev) => ({ ...prev, condicionEntrega: value }));
        if (!idCotizacion) {
            notify('warn', 'Cotización', 'ID de cotización no disponible');
            return;
        }
        const condicion = value === 'DESPACHO' ? 4 : 3;
        try {
            if (condicion === 3) {
                await actualizarDespacho(idCotizacion, false);
            }
            await actualizarEntrega(idCotizacion, condicion);
            await handleRefresh();
        } catch (error) {
            console.error('Error actualizando condición de entrega:', error);
            notify('error', 'Error actualizando condición de entrega', error.message || '');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateLine = async (rowIndex, field, value) => {
        setLoading(true);
        const line = detallesState[rowIndex];
        if (!line) return;

        if (field === 'descuento') {
            try {
                await actualizarDescuento(value ?? 0, line.id);
                await handleRefresh();
            } catch (error) {
                console.error('Error actualizando descuento:', error);
                notify('error', 'Error actualizando descuento', error.message || '');
            }
        } else if (field === 'precio') {
            try {
                await actualizarPrecio(line.id, value ?? 0);
                await handleRefresh();
            } catch (error) {
                console.error('Error actualizando precio:', error);
                notify('error', 'Error actualizando precio', error.message || '');
            }
        } else if (field === 'centro') {
            const nextDetalles = [...detallesState];
            nextDetalles[rowIndex] = { ...nextDetalles[rowIndex], centro: value };

            if (!idCotizacion || !line.id) {
                setDetallesState(nextDetalles);
                return;
            }

            try {
                await getAlmacen(line.id, value);
                const existencia = await existenciaCotizacion(idCotizacion);
                if (existencia) {
                    const { updatedLines, almacenesList } = applyExistenciaStocks(existencia, nextDetalles);
                    setAlmacenes(almacenesList);
                    setDetallesState(updatedLines);
                } else {
                    setDetallesState(nextDetalles);
                }
            } catch (error) {
                console.error('Error actualizando centro:', error);
                notify('error', 'Error actualizando centro', error.message || '');
                setDetallesState(nextDetalles);
            }
        } else {
            const nextDetalles = [...detallesState];
            nextDetalles[rowIndex] = { ...nextDetalles[rowIndex], [field]: value };
            setDetallesState(nextDetalles);
        }
        setLoading(false);
    };

    const handleUpdateStocks = (existencia) => {
        setLoading(true);
        if (!existencia) return;
        const { updatedLines, almacenesList } = applyExistenciaStocks(existencia, detallesState);
        setAlmacenes(almacenesList);
        setDetallesState(updatedLines);
        setLoading(false);
    };

    const productosTable = () => {
        setVisibleProductosTable(true);
    };

    return (
        <Dialog 
            header={`Editar Cotización Folio: ${cotizacion?.folio || ''}`}
            visible={visible} 
            
            onHide={onCancel}
        >
            <div className="card">
                <CabeceraCliente cotizacion={cotizacion} />
                
                <div className="mb-3 border-top-1 surface-border pt-3">
                    {loading && <LoaderTransparent coverParent overlayTint="transparent" />}
                    <TableDetalleCotizacion
                        idCotizacion={idCotizacion}
                        detalles={detallesState}
                        relacionados={null}
                        onUpdateLine={handleUpdateLine}
                        onRemoveLine={handleRemoveLine}
                        tipo={tipo}
                        proyectos={null}
                        proyecto={null}
                        onRefresh={handleRefresh}
                        onChangeCantidad={handleChangeCantidad}
                        onNotify={notify}
                        confirming={confirming}
                        almacenes={almacenes}
                        edit={edit}
                        setLoading={setLoading}
                    />
                </div>
                <div className="mb-3 border-top-1 surface-border pt-3">
                    <ObservacionesEntregaResumen
                        data={data}
                        setData={setData}
                        idCotizacion={idCotizacion}
                        notify={notify}
                        resumen={resumen}
                        setResumen={setResumen}
                        onChangeCondicionEntrega={onChangeCondicionEntrega}
                        onRefresh={handleRefresh}
                    />
                </div>
                <div className="mb-3 border-top-1 surface-border pt-3">
                    <BotoneraCotizacion
                        tipo={tipo}
                        onAgregarMaterial={productosTable}
                        idCotizacion={idCotizacion}
                        onRefresh={handleRefresh}
                        onNotify={notify}
                        setIsDetalleVisible={() => onCancel && onCancel()}
                        loading={loading}
                        setLoading={setLoading}
                        confirming={confirming}
                        setConfirming={setConfirming}
                        onUpdateStocks={handleUpdateStocks}
                        edit={edit}
                    />
                </div>

                <TableProductos
                        visible={visibleProductosTable}
                        onHide={() => setVisibleProductosTable(false)}
                        onCancel={() => setVisibleProductosTable(false)}
                        idCotizacion={idCotizacion}
                        tipo={tipo}
                        onAdded={() => fetchData()}
                        onNotify={notify}
                        loading={loading}
                        setLoading={setLoading}
                    />
            </div>
        </Dialog>
    );
};